import { useEffect, useState } from "react";

import { useMutation } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { Form, Modal } from "react-bootstrap";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdReportProblem } from "react-icons/md";
import type { SingleValue } from "react-select";
import * as z from "zod/v4";

import { may } from "@/abilities";
import { addDangerFlash, addSuccessFlash } from "@/components/flash/flashReducer";
import TTModal from "@/components/TTModal";
import TTSelect from "@/components/TTSelect";
import { CancelButton, SaveButton } from "@/containers/buttons";
import RHInput from "@/containers/RHInput";
import TTFormGroup from "@/containers/TTFormGroup";
import { MutateTicketDocument } from "@/gql/graphql";
import handleError from "@/handleError";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { sessionSelector } from "@/store/sessionReducer";

import TTMarkdownEditor from "../TTMarkdownEditor";

const validationSchema = z.object({
  subject: z.string().trim().nonempty("Bitte geben Sie einen Betreff an!"),
  status: z.enum([
    "NEW",
    "ACCEPTED",
    "IN_PROGRESS",
    "QUESTION",
    "RESOLVED",
    "NOTABUG",
    "WONTFIX",
    "DUPLICATE",
    "REVIEWED",
    "ONHOLD",
  ]),
  category: z.enum([
    "USERS",
    "RIGHTS",
    "BUG",
    "ENHANCEMENT",
    "CONTRACT",
    "HARDWARE",
    "TECHNICAL_PROBLEM",
    "IT_QUESTION",
    "USBINTERFACE",
    "DATA_IMPORT",
  ]),
  priority: z.number(),
  ownerId: z.string().optional(),
  watchers: z.array(z.string()),
  description: z.string().trim(),
  visible: z.boolean(),
  mdContent: z.string().trim().nonempty("Bitte geben Sie eine Beschreibung an!"),
  attrs: z.object({
    problemType: z.string().trim().nonempty("Bitte wählen Sie ein Problem aus"),
    workstation: z.string().trim().nonempty("Bitte geben Sie die Workstation an"),
  }),
});

export type ValuesType = z.infer<typeof validationSchema>;

const C_RICHTER = "4";
const M_RAHIMI = "3040";

const INITIAL_VALUES: ValuesType = {
  subject: "",
  status: "NEW",
  category: "TECHNICAL_PROBLEM",
  priority: 2,
  watchers: [C_RICHTER, M_RAHIMI],
  visible: true,
  attrs: {
    problemType: "",
    workstation: "",
  },
  description: "",
  mdContent: "",
};

const PROBLEM_TYPES = [
  { label: "Kein Ton", value: "NO_SOUND" },
  { label: "Kamera geht nicht", value: "CAMERA_NOT_WORKING" },
  { label: "Monitor bleibt schwarz", value: "BLACK_SCREEN" },
  { label: "Status lässt sich nicht ändern", value: "STATUS_NOT_CHANGING" },
  { label: "Mitarbeiter kann sich nicht anmelden", value: "LOGIN_PROBLEM" },
  { label: "anderes Problem", value: "OTHER" },
];

export default function ITProblemModal() {
  const session = useAppSelector(sessionSelector);
  const dispatch = useAppDispatch();
  const [show, setShow] = useState(false);
  const { t } = useTranslation(["translation"]);

  const [mutateTicket] = useMutation(MutateTicketDocument);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<ValuesType>({
    resolver: zodResolver(validationSchema),
    defaultValues: INITIAL_VALUES,
    mode: "onBlur",
  });

  const mdContent = useWatch({ control, name: "mdContent" });
  const problemType = useWatch({ control, name: "attrs.problemType" });
  const workstation = useWatch({ control, name: "attrs.workstation" });

  useEffect(
    () => {
      if (show) {
        reset(INITIAL_VALUES);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [show],
  );

  if (!may(session, "tickets", "new") || session.currentUser.customerId !== "1") {
    return null;
  }

  async function createTicket(values: ValuesType) {
    try {
      await mutateTicket({
        variables: {
          customerId: "1",
          values,
        },
      });

      dispatch(addSuccessFlash("Das Problem wurde erfolgreich gemeldet!"));
      setShow(false);
    } catch (error) {
      dispatch(addDangerFlash(t("translation:global.general_error")));
      handleError(error);
    }
  }

  function updateValues(value: SingleValue<(typeof PROBLEM_TYPES)[number]>) {
    if (!value) {
      return;
    }

    setValue("attrs.problemType", value.value);
    setValue("mdContent", `Bei Workstation ${workstation || "…"} ist folgendes Problem aufgetreten: ${value.label}`);

    if (value.value !== "OTHER") {
      setValue("subject", PROBLEM_TYPES.find((problem) => problem.value === value.value)?.label || "");
    }
  }

  function updateDescription(event: React.ChangeEvent<HTMLInputElement>) {
    const problemTypeLabel = PROBLEM_TYPES.find((problem) => problem.value === problemType)?.label || "…";

    setValue("attrs.workstation", event.target.value);
    setValue(
      "mdContent",
      `Bei Workstation ${event.target.value} ist folgendes Problem aufgetreten: ${problemTypeLabel}`,
    );
  }

  function hideModal() {
    setShow(false);
  }

  return (
    <>
      <button type="button" onClick={() => setShow(true)} title="IT-Problem melden">
        <MdReportProblem />
      </button>

      {show && (
        <TTModal size="lg" show={show} onHide={hideModal}>
          <Form onSubmit={handleSubmit(createTicket)}>
            <Modal.Header closeButton>
              <Modal.Title>IT-Problem melden</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <TTFormGroup>
                <Form.Label htmlFor="attrs.problemType">Problem</Form.Label>
                <TTSelect
                  id="attrs.problemType"
                  name="attrs.problemType"
                  placeholder="Problem wählen…"
                  onChange={updateValues}
                  options={PROBLEM_TYPES}
                  className={clsx("TT-RH-react-select", {
                    "is-invalid": !!errors.attrs?.problemType,
                    "is-valid": !errors.attrs?.problemType,
                  })}
                />
              </TTFormGroup>

              <TTFormGroup>
                <Form.Label htmlFor="attrs.workstation">Workstation</Form.Label>
                <RHInput
                  control={control}
                  id="attrs.workstation"
                  name="attrs.workstation"
                  type="text"
                  placeholder="Workstation eingeben…"
                  onChange={updateDescription}
                />
                <Form.Text className="text-muted">
                  Die Nummer der Workstation steht auf dem Aufkleber am Computer, z.B. „WS01“
                </Form.Text>
              </TTFormGroup>

              {problemType === "OTHER" && (
                <RHInput
                  control={control}
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="Betreff eingeben…"
                  label="Betreff"
                />
              )}

              <TTFormGroup>
                <Form.Label htmlFor="mdContent">Beschreibung</Form.Label>
                <TTMarkdownEditor value={mdContent} onChange={(v) => setValue("mdContent", v)} />
              </TTFormGroup>
            </Modal.Body>

            <Modal.Footer>
              <SaveButton disabled={isSubmitting} type="submit">
                Problem melden
              </SaveButton>
              <CancelButton disabled={isSubmitting} type="button" onClick={hideModal}>
                abbrechen
              </CancelButton>
            </Modal.Footer>
          </Form>
        </TTModal>
      )}
    </>
  );
}
